import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SecurityContext,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterLink } from '@angular/router';
import { Popover } from 'bootstrap';

@Component({
  selector: 'app-generic-card',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './generic-card.component.html',
  styleUrl: './generic-card.component.css',
})
export class GenericCardComponent implements AfterViewInit, OnChanges, OnDestroy {
  private sanitizer: DomSanitizer = inject(DomSanitizer);
  private router: Router = inject(Router);

  @Input() title: string = '';
  @Input({ transform: (value: string | undefined): string => (value ? value : '') })
  description: string = '';
  @Input() showHeader: boolean = true;
  @Input() showFooter: boolean = true;
  @Input() showTitle: boolean = true;
  @Input() showDescription: boolean = true;
  @Input() clampDescription: boolean = true;
  @Input() detailLink: string | readonly (string | number)[] | null = null;
  @Input() canEdit: boolean = false;
  @Input() editLabel: string = 'Puoi modificare';
  @Input() editLink: string | readonly (string | number)[] | null = null;
  @Input() editAriaLabel: string = 'Modifica';

  @ViewChild('descriptionEl') private descriptionEl?: ElementRef<HTMLElement>;

  protected readonly isDescriptionClamped = signal(false);
  private descriptionPopover?: Popover;

  ngOnChanges(): void {
    queueMicrotask(() => this.refreshDescriptionOverflow());
  }

  ngAfterViewInit(): void {
    this.refreshDescriptionOverflow();
  }

  ngOnDestroy(): void {
    this.descriptionPopover?.dispose();
  }

  protected get isClickable(): boolean {
    if (this.detailLink == null) {
      return false;
    }
    return typeof this.detailLink === 'string'
      ? this.detailLink.length > 0
      : this.detailLink.length > 0;
  }

  protected onCardClick(event: Event): void {
    if (!this.isClickable || this.isInteractiveTarget(event)) {
      return;
    }
    this.navigateToDetail();
  }

  protected onCardKeydown(event: KeyboardEvent): void {
    if (!this.isClickable || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }
    if (this.isInteractiveTarget(event)) {
      return;
    }
    event.preventDefault();
    this.navigateToDetail();
  }

  protected onDescriptionClick(event: Event): void {
    event.stopPropagation();
  }

  protected get sanitizedDescription(): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, this.description ?? '') ?? '';
  }

  private isInteractiveTarget(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest(
      'a, button, input, select, textarea, label, [role="button"], .generic-card-description, app-osm-map, app-image-lightbox',
    );
  }

  private navigateToDetail(): void {
    if (!this.detailLink) {
      return;
    }
    const commands = typeof this.detailLink === 'string' ? [this.detailLink] : [...this.detailLink];
    void this.router.navigate(commands);
  }

  private refreshDescriptionOverflow(): void {
    this.descriptionPopover?.dispose();
    this.descriptionPopover = undefined;

    const el = this.descriptionEl?.nativeElement;
    if (!el || !this.clampDescription || !this.showDescription || !this.description) {
      this.isDescriptionClamped.set(false);
      return;
    }

    const clamped = el.scrollHeight > el.clientHeight + 1;
    this.isDescriptionClamped.set(clamped);
    if (!clamped) {
      return;
    }

    this.descriptionPopover = new Popover(el, {
      html: true,
      sanitize: false,
      trigger: 'hover focus',
      placement: 'auto',
      container: 'body',
      title: 'Descrizione',
      content: this.sanitizedDescription,
      customClass: 'generic-card-description-popover',
    });
  }
}
